import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import AdminNavigation from '@/components/AdminNavigation';
import { LoadingState } from '@/components/LoadingState';
import {
  RefreshCw,
  Users,
  Database,
  RotateCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  BarChart3,
  Settings,
  Play,
  Pause,
  Search,
  Filter,
  Download,
  Upload,
  Loader2,
  CreditCard,
  Shield,
  Crown,
  Eye,
  Calendar,
  TrendingUp
} from 'lucide-react';

interface User {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  stripeCustomerId: string | null;
  subscriptionStatus: string;
  subscriptionSource: string | null;
  isOfficialBearInsider: boolean;
  badgeCount?: number;
  lastSyncedAt?: string;
  syncStatus?: 'synced' | 'pending' | 'failed' | 'never';
  syncError?: string;
}

interface SyncOperation {
  id: string;
  type: 'user' | 'subscription' | 'full';
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  total: number;
  completed: number;
  errors: string[];
  startedAt: string;
  completedAt?: string;
  estimatedTimeRemaining?: number;
}

interface SyncStats {
  totalUsers: number;
  syncedUsers: number;
  pendingUsers: number;
  failedUsers: number;
  lastFullSyncAt?: string;
  averageSyncTime: number;
  successRate: number;
}

const SyncStatusBadge = ({ status }: { status: string }) => {
  const getBadgeConfig = () => {
    switch (status) {
      case 'synced':
        return { variant: 'default', icon: CheckCircle, color: 'text-green-500' };
      case 'pending':
        return { variant: 'secondary', icon: Clock, color: 'text-yellow-500' };
      case 'failed':
        return { variant: 'destructive', icon: XCircle, color: 'text-red-500' };
      case 'never':
        return { variant: 'outline', icon: AlertTriangle, color: 'text-gray-500' };
      default:
        return { variant: 'outline', icon: Clock, color: 'text-gray-500' };
    }
  };

  const { variant, icon: Icon, color } = getBadgeConfig();

  return (
    <Badge variant={variant as any} className="flex items-center gap-1">
      <Icon className={`h-3 w-3 ${color}`} />
      {status}
    </Badge>
  );
};

export default function AdminStripeSync() {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [showBulkSyncDialog, setShowBulkSyncDialog] = useState(false);
  const [activeSyncOperation, setActiveSyncOperation] = useState<SyncOperation | null>(null);
  const [syncType, setSyncType] = useState<'users' | 'subscriptions' | 'full'>('users');

  // Get sync statistics
  const { data: syncStats, refetch: refetchStats } = useQuery({
    queryKey: ['/api/admin/stripe-sync/stats'],
    select: (response: any) => response.data as SyncStats,
  });

  // Get users with sync status
  const { data: usersData, isLoading: usersLoading, refetch: refetchUsers } = useQuery({
    queryKey: ['/api/admin/stripe-sync/users', searchTerm, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('syncStatus', statusFilter);
      params.append('limit', '100');
      return fetch(`/api/admin/stripe-sync/users?${params}`).then(res => res.json());
    },
    select: (response) => response.data as User[],
  });

  // Get active sync operations
  const { data: syncOperations, refetch: refetchOperations } = useQuery({
    queryKey: ['/api/admin/stripe-sync/operations'],
    select: (response: any) => response.data as SyncOperation[],
    refetchInterval: (query) => {
      // Refetch every 2 seconds if there are active operations
      const data = query.state.data;
      return (data && Array.isArray(data) && data.some((op: SyncOperation) => op.status === 'running')) ? 2000 : false;
    },
  });

  // Individual user sync mutation
  const syncUserMutation = useMutation({
    mutationFn: (userId: number) =>
      apiRequest('POST', `/api/admin/stripe-sync/users/${userId}`),
    onSuccess: () => {
      toast({ title: 'Success', description: 'User synced successfully' });
      refetchUsers();
      refetchStats();
    },
    onError: (error: any) => {
      toast({ 
        title: 'Sync failed', 
        description: error.message, 
        variant: 'destructive' 
      });
    },
  });

  // Bulk sync mutation
  const bulkSyncMutation = useMutation({
    mutationFn: ({ userIds, type }: { userIds: number[]; type: string }) =>
      apiRequest('POST', '/api/admin/stripe-sync/bulk', { userIds, type }),
    onSuccess: (response: any) => {
      setActiveSyncOperation(response.data);
      toast({ title: 'Bulk sync started', description: `Syncing ${selectedUsers.size} users` });
      setShowBulkSyncDialog(false);
      setSelectedUsers(new Set());
    },
    onError: (error: any) => {
      toast({ 
        title: 'Bulk sync failed', 
        description: error.message, 
        variant: 'destructive' 
      });
    },
  });

  // Full sync mutation
  const fullSyncMutation = useMutation({
    mutationFn: () => apiRequest('POST', '/api/admin/stripe-sync/full'),
    onSuccess: (response: any) => {
      setActiveSyncOperation(response.data);
      toast({ title: 'Full sync started', description: 'Syncing all users and data' });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Full sync failed', 
        description: error.message, 
        variant: 'destructive' 
      });
    },
  });

  const users = usersData || [];
  const stats = syncStats || {
    totalUsers: 0,
    syncedUsers: 0,
    pendingUsers: 0,
    failedUsers: 0,
    averageSyncTime: 0,
    successRate: 0,
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || user.syncStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (userId: number, checked: boolean) => {
    const newSelected = new Set(selectedUsers);
    if (checked) {
      newSelected.add(userId);
    } else {
      newSelected.delete(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleBulkSync = () => {
    bulkSyncMutation.mutate({
      userIds: Array.from(selectedUsers),
      type: syncType,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminNavigation currentPage="Stripe Sync" />
      
      <div className="container mx-auto px-4 py-6">
        <div className="space-y-6">
          
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Stripe Synchronization
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage customer data synchronization between app and Stripe
              </p>
            </div>
            
            <div className="flex space-x-2">
              <Button 
                onClick={() => refetchUsers()} 
                variant="outline" 
                data-testid="button-refresh"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button 
                onClick={() => fullSyncMutation.mutate()}
                disabled={fullSyncMutation.isPending}
                data-testid="button-full-sync"
              >
                {fullSyncMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Database className="h-4 w-4 mr-2" />
                )}
                Full Sync
              </Button>
            </div>
          </div>

          {/* Active Sync Operations */}
          {syncOperations && syncOperations.some(op => op.status === 'running') && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertDescription>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span>Sync operation in progress...</span>
                    <Badge variant="secondary">
                      {activeSyncOperation?.completed || 0}/{activeSyncOperation?.total || 0}
                    </Badge>
                  </div>
                  <Progress 
                    value={activeSyncOperation?.progress || 0} 
                    className="w-full" 
                    data-testid="sync-progress"
                  />
                  {activeSyncOperation?.estimatedTimeRemaining && (
                    <p className="text-sm text-gray-500">
                      Estimated time remaining: {Math.ceil(activeSyncOperation.estimatedTimeRemaining / 60)}m
                    </p>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
              <TabsTrigger value="operations" data-testid="tab-operations">Operations</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Sync Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card data-testid="stat-total-users">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.totalUsers}</div>
                  </CardContent>
                </Card>

                <Card data-testid="stat-synced-users">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Synced</CardTitle>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">{stats.syncedUsers}</div>
                    <p className="text-xs text-muted-foreground">
                      {stats.totalUsers > 0 ? Math.round((stats.syncedUsers / stats.totalUsers) * 100) : 0}% success rate
                    </p>
                  </CardContent>
                </Card>

                <Card data-testid="stat-pending-users">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Pending</CardTitle>
                    <Clock className="h-4 w-4 text-yellow-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-600">{stats.pendingUsers}</div>
                    <p className="text-xs text-muted-foreground">Awaiting sync</p>
                  </CardContent>
                </Card>

                <Card data-testid="stat-failed-users">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Failed</CardTitle>
                    <XCircle className="h-4 w-4 text-red-500" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-red-600">{stats.failedUsers}</div>
                    <p className="text-xs text-muted-foreground">Require attention</p>
                  </CardContent>
                </Card>
              </div>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Button 
                    className="h-20 flex flex-col items-center justify-center space-y-2"
                    onClick={() => fullSyncMutation.mutate()}
                    disabled={fullSyncMutation.isPending}
                    data-testid="action-full-sync"
                  >
                    <Database className="h-6 w-6" />
                    <span>Full Sync</span>
                  </Button>
                  
                  <Button 
                    variant="outline"
                    className="h-20 flex flex-col items-center justify-center space-y-2"
                    onClick={() => {/* Navigate to failed users */}}
                    data-testid="action-retry-failed"
                  >
                    <RefreshCw className="h-6 w-6" />
                    <span>Retry Failed</span>
                  </Button>
                  
                  <Button 
                    variant="outline"
                    className="h-20 flex flex-col items-center justify-center space-y-2"
                    onClick={() => {/* Export sync report */}}
                    data-testid="action-export-report"
                  >
                    <Download className="h-6 w-6" />
                    <span>Export Report</span>
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users" className="space-y-6">
              {/* Filters and Bulk Actions */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          placeholder="Search users by email, name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                          data-testid="input-search-users"
                        />
                      </div>
                    </div>
                    
                    {/* Status Filter */}
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-48" data-testid="select-status-filter">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="synced">Synced</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="never">Never Synced</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Bulk Actions */}
                    {selectedUsers.size > 0 && (
                      <Button 
                        onClick={() => setShowBulkSyncDialog(true)}
                        data-testid="button-bulk-sync"
                      >
                        <RotateCw className="h-4 w-4 mr-2" />
                        Sync ({selectedUsers.size})
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Users Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Users ({filteredUsers.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {usersLoading ? (
                    <LoadingState message="Loading users..." />
                  ) : filteredUsers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-12">
                              <Checkbox
                                checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                                onCheckedChange={handleSelectAll}
                                data-testid="checkbox-select-all"
                              />
                            </TableHead>
                            <TableHead>User</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Subscription</TableHead>
                            <TableHead>Sync Status</TableHead>
                            <TableHead>Last Sync</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredUsers.map((user) => (
                            <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                              <TableCell>
                                <Checkbox
                                  checked={selectedUsers.has(user.id)}
                                  onCheckedChange={(checked) => handleSelectUser(user.id, checked as boolean)}
                                  data-testid={`checkbox-user-${user.id}`}
                                />
                              </TableCell>
                              
                              <TableCell>
                                <div className="flex items-center space-x-2">
                                  <div>
                                    <p className="font-medium">
                                      {user.firstName} {user.lastName}
                                    </p>
                                    <p className="text-sm text-gray-500">ID: {user.id}</p>
                                  </div>
                                  {user.isOfficialBearInsider && (
                                    <Crown className="h-4 w-4 text-yellow-500" />
                                  )}
                                </div>
                              </TableCell>
                              
                              <TableCell>{user.email}</TableCell>
                              
                              <TableCell>
                                <Badge 
                                  variant={user.subscriptionStatus === 'active' || user.subscriptionStatus === 'lifetime' ? 'default' : 'secondary'}
                                >
                                  {user.subscriptionStatus}
                                </Badge>
                              </TableCell>
                              
                              <TableCell>
                                <SyncStatusBadge status={user.syncStatus || 'never'} />
                                {user.syncError && (
                                  <p className="text-xs text-red-500 mt-1">{user.syncError}</p>
                                )}
                              </TableCell>
                              
                              <TableCell>
                                {user.lastSyncedAt ? (
                                  <div className="flex items-center space-x-1">
                                    <Calendar className="h-4 w-4 text-gray-400" />
                                    <span className="text-sm">
                                      {new Date(user.lastSyncedAt).toLocaleDateString()}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-gray-400">Never</span>
                                )}
                              </TableCell>
                              
                              <TableCell>
                                <div className="flex space-x-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => syncUserMutation.mutate(user.id)}
                                    disabled={syncUserMutation.isPending}
                                    data-testid={`button-sync-${user.id}`}
                                  >
                                    {syncUserMutation.isPending ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <RotateCw className="h-3 w-3" />
                                    )}
                                  </Button>
                                  
                                  {user.stripeCustomerId && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => window.open(`https://dashboard.stripe.com/customers/${user.stripeCustomerId}`, '_blank')}
                                      data-testid={`button-view-stripe-${user.id}`}
                                    >
                                      <Eye className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No users found</p>
                      <p className="text-sm">
                        {searchTerm || statusFilter !== 'all' 
                          ? 'Try adjusting your search criteria' 
                          : 'No users available for sync'
                        }
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Operations Tab */}
            <TabsContent value="operations" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Sync Operations</CardTitle>
                </CardHeader>
                <CardContent>
                  {syncOperations && syncOperations.length > 0 ? (
                    <div className="space-y-4">
                      {syncOperations.map((operation) => (
                        <div 
                          key={operation.id} 
                          className="border rounded-lg p-4"
                          data-testid={`operation-${operation.id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Badge 
                                variant={
                                  operation.status === 'completed' ? 'default' :
                                  operation.status === 'failed' ? 'destructive' :
                                  operation.status === 'running' ? 'secondary' : 'outline'
                                }
                              >
                                {operation.status}
                              </Badge>
                              <span className="font-medium capitalize">{operation.type} Sync</span>
                            </div>
                            <div className="text-sm text-gray-500">
                              {new Date(operation.startedAt).toLocaleString()}
                            </div>
                          </div>
                          
                          {operation.status === 'running' && (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span>Progress: {operation.completed}/{operation.total}</span>
                                <span>{Math.round(operation.progress)}%</span>
                              </div>
                              <Progress value={operation.progress} className="w-full" />
                            </div>
                          )}
                          
                          {operation.errors.length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm font-medium text-red-600">
                                Errors ({operation.errors.length}):
                              </p>
                              <div className="text-xs text-red-500 max-h-20 overflow-y-auto">
                                {operation.errors.slice(0, 3).map((error, idx) => (
                                  <p key={idx}>• {error}</p>
                                ))}
                                {operation.errors.length > 3 && (
                                  <p>... and {operation.errors.length - 3} more</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No sync operations yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card data-testid="analytics-success-rate">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <TrendingUp className="h-5 w-5" />
                      <span>Success Rate</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold mb-2">
                      {Math.round(stats.successRate)}%
                    </div>
                    <Progress value={stats.successRate} className="w-full" />
                    <p className="text-sm text-gray-500 mt-2">
                      {stats.syncedUsers} of {stats.totalUsers} users synced successfully
                    </p>
                  </CardContent>
                </Card>

                <Card data-testid="analytics-avg-sync-time">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Clock className="h-5 w-5" />
                      <span>Average Sync Time</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold mb-2">
                      {stats.averageSyncTime.toFixed(1)}s
                    </div>
                    <p className="text-sm text-gray-500">
                      Per user synchronization
                    </p>
                  </CardContent>
                </Card>
              </div>

              {stats.lastFullSyncAt && (
                <Card>
                  <CardHeader>
                    <CardTitle>Last Full Sync</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-lg">
                      {new Date(stats.lastFullSyncAt).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500">
                      {Math.floor((Date.now() - new Date(stats.lastFullSyncAt).getTime()) / (1000 * 60 * 60))} hours ago
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Bulk Sync Dialog */}
      <Dialog open={showBulkSyncDialog} onOpenChange={setShowBulkSyncDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Sync Operations</DialogTitle>
            <DialogDescription>
              Sync {selectedUsers.size} selected users with Stripe
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Sync Type</label>
              <Select value={syncType} onValueChange={(value: any) => setSyncType(value)}>
                <SelectTrigger data-testid="select-bulk-sync-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="users">Users Only</SelectItem>
                  <SelectItem value="subscriptions">Subscriptions Only</SelectItem>
                  <SelectItem value="full">Full Sync</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button 
                variant="outline" 
                onClick={() => setShowBulkSyncDialog(false)}
                data-testid="button-cancel-bulk-sync"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleBulkSync}
                disabled={bulkSyncMutation.isPending}
                data-testid="button-confirm-bulk-sync"
              >
                {bulkSyncMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCw className="h-4 w-4 mr-2" />
                )}
                Start Sync
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}